import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/api/user';
import { unauthenticatedLoginPath } from '@/auth/sessionEnded';
import { LoadingOverlay } from '@/components/LoadingOverlay';

/**
 * Gate for authenticated routes. Replaces the server-side
 * `requireAuth(request)` / `redirect('/login')` guard that loaders used to do.
 *
 * Two gates:
 *  1. Auth0 session — while resolving render `fallback`; genuinely
 *     unauthenticated users land on the app's own /login page, preserving
 *     where they were headed. Never straight on Auth0: its login transaction
 *     must be created by the user's click, not by a page load (see
 *     sessionEnded.ts).
 *  2. Backend user — once authenticated we also wait for the `useCurrentUser`
 *     query before rendering children. Protected pages assume the user is
 *     present (e.g. `useFeatureFlags` does `invariant(user)`), so rendering them
 *     mid-load would throw "User not found" and bounce through RouteError. A
 *     genuine fetch failure still falls through to that handler.
 *
 * Gate 1 does NOT trust `isAuthenticated === false` on its own. On a reload the
 * SDK (`useRefreshTokens` + `localstorage`) reports `false` for a beat while it
 * restores the session from the cached refresh token; redirecting on that boolean
 * bounced logged-in users to the Auth0 login form (which then shows because a
 * refresh-token SPA has no SSO cookie to silently round-trip). Instead we
 * actively call `getAccessTokenSilently()` — it uses the refresh token to
 * restore/renew and only rejects when auth is genuinely unrecoverable
 * (`login_required` / `missing_refresh_token`), which is the only case that
 * warrants sending them to log in.
 */
export function ProtectedRoute({
    children,
    fallback = null,
}: {
    children: ReactNode;
    fallback?: ReactNode;
}) {
    const {
        isLoading,
        isAuthenticated,
        error: authError,
        getAccessTokenSilently,
    } = useAuth0();
    const navigate = useNavigate();
    const userQuery = useCurrentUser();

    const verifying = useRef(false);
    const [authFailed, setAuthFailed] = useState(false);

    useEffect(() => {
        // Only verify once the SDK has initialised and only if it isn't already
        // reporting an authenticated session. A successful silent acquisition
        // flips `isAuthenticated` to true via the SDK; only a genuine rejection
        // means the session is unrecoverable. The ref guards against a second
        // in-flight request.
        // A redirect-callback error (e.g. org access_denied, failed code
        // exchange) is handled by AuthErrorGate — restarting login here would
        // just loop through the same failure.
        if (isLoading || isAuthenticated || authError || verifying.current)
            return;
        verifying.current = true;
        getAccessTokenSilently()
            .then(() => {
                verifying.current = false;
            })
            .catch(() => setAuthFailed(true));
    }, [isLoading, isAuthenticated, authError, getAccessTokenSilently]);

    useEffect(() => {
        if (authFailed) {
            const { pathname, search } = window.location;
            navigate(unauthenticatedLoginPath(pathname, search), {
                replace: true,
            });
        }
    }, [authFailed, navigate]);

    // Gate 1: initialising, silently restoring, or heading to /login after a
    // failed restore — hold on `fallback` rather than rendering protected
    // children.
    // Rendering only once the *real* `isAuthenticated` is true guarantees
    // `useCurrentUser` is enabled below (it's `enabled: isAuthenticated`).
    if (isLoading || !isAuthenticated) return <>{fallback}</>;

    // Gate 2: authenticated, but wait for the backend user before rendering.
    // A disabled/pending query reports `isLoading: false` with `data: undefined`
    // (React Query v5), so gate on "no data yet and no error" — NOT on
    // `isLoading` — otherwise pages that assume the user is present (e.g.
    // ClientDetailPage throws "User not found") bail to /login mid-load. A
    // genuine fetch error falls through so the route boundary can handle it.
    if (!userQuery.data && !userQuery.isError) return <LoadingOverlay />;

    return <>{children}</>;
}
